import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export function Card({ title, desc, children }) {
  return (
    <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6 sm:p-8" data-testid={title ? `admin-card-${title.toLowerCase().replace(/\s+/g, "-")}` : undefined}>
      {title && <h2 className="font-display font-extrabold text-white text-xl">{title}</h2>}
      {desc && <p className="mt-1 text-sm text-slate-400 mb-6">{desc}</p>}
      <div className={title && desc ? "" : "mt-0"}>{children}</div>
    </div>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}

export function TextInput({ value, onChange, type = "text", placeholder = "", className = "", ...rest }) {
  return <input className={`admin-input ${className}`} type={type} value={value} placeholder={placeholder} onChange={onChange} {...rest} />;
}

export function TextArea({ value, onChange, rows = 3, ...rest }) {
  return <textarea className="admin-input" rows={rows} value={value} onChange={onChange} {...rest} />;
}

export function Toggle({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-3 py-2"
      data-testid={`toggle-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className={`relative w-10 h-6 rounded-full transition-colors duration-200 ${checked ? "bg-[#00BFFF]" : "bg-slate-600"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200 ${checked ? "left-[18px]" : "left-0.5"}`} />
      </span>
      <span className="text-sm text-slate-300">{label}</span>
    </button>
  );
}

export function SaveButton({ onClick, saving, label = "Save & Go Live" }) {
  return (
    <button
      className="btn btn-sm btn-primary mt-6"
      onClick={onClick}
      disabled={saving}
      data-testid="admin-save-button"
    >
      {saving ? <Loader2 size={15} className="animate-spin" /> : null}
      {saving ? "Saving..." : label}
    </button>
  );
}

export function useContentSaver(refresh) {
  const [saving, setSaving] = useState(false);
  const save = async (key, data) => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${key}`, { data });
      toast.success("Saved — live on the site now");
      refresh && refresh();
    } catch (e) {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  return { saving, save };
}

export function useFetch(url) {
  const [data, setData] = useState(null);
  const load = () => {
    api.get(url).then(({ data }) => setData(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [url]); // eslint-disable-line
  return [data, () => api.get(url).then(({ data }) => setData(data))];
}
